import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { buildLiveScreenSize, buildLiveScreenX, buildLiveScreenY } from '#lib/layer-geometry';
import { buildGridPreviewZones, GridGenerationRuntime } from '#lib/grid-generation-script';

interface GridGenerationTilesPreviewOverlayProps {
  projectId: string;
  workspaceId: string;
  runtime: GridGenerationRuntime | null;
  enabled: boolean;
  activeIndex: number;
}

const GridGenerationTilesPreviewOverlay: React.FC<GridGenerationTilesPreviewOverlayProps> = ({
  projectId,
  workspaceId,
  runtime,
  enabled,
  activeIndex,
}) => {
  const { kernel } = useKernel();
  const zoomLevel = useKernelStore((state) => {
    const value = state.inventory.Project.registry[projectId]?.runtime?.zoomLevel;
    return typeof value === 'number' && Number.isFinite(value) ? value : 1;
  });
  const panOffset = useKernelStore((state) => {
    const value = state.inventory.Project.registry[projectId]?.runtime?.panOffset;
    if (
      value &&
      typeof value === 'object' &&
      Number.isFinite((value as { x?: unknown }).x) &&
      Number.isFinite((value as { y?: unknown }).y)
    ) {
      return value as { x: number; y: number };
    }
    return { x: 0, y: 0 };
  });
  const generationRect = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.generationRect ?? null
  );
  const frameRect = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect ?? null
  );

  const zones = useMemo(() => {
    if (!enabled || !runtime || !generationRect) return [];
    if (!(generationRect.w > 0 && generationRect.h > 0)) return [];
    return buildGridPreviewZones(generationRect, runtime, frameRect);
  }, [enabled, runtime, generationRect, frameRect]);
  const normalizedActiveIndex = zones.length > 0
    ? ((activeIndex % zones.length) + zones.length) % zones.length
    : 0;
  const activeZone = zones[normalizedActiveIndex] ?? null;

  const portalHost = kernel.current?.canvasContainerRef.current ?? null;
  if (!portalHost || !enabled || !runtime || !generationRect || zones.length === 0) return null;

  return createPortal(
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 22,
      }}
      aria-hidden="true"
    >
      <style>
        {`
          @keyframes fater-grid-preview-pulse {
            0% { background-color: rgba(70, 70, 70, 0.05); }
            100% { background-color: rgba(70, 70, 70, 0.35); }
          }
        `}
      </style>

      {activeZone && (
        <div
          style={{
            position: 'absolute',
            left: buildLiveScreenX(activeZone.rect.x, panOffset.x, zoomLevel),
            top: buildLiveScreenY(activeZone.rect.y, panOffset.y, zoomLevel),
            width: buildLiveScreenSize(activeZone.rect.w, zoomLevel),
            height: buildLiveScreenSize(activeZone.rect.h, zoomLevel),
            borderRadius: 0,
            boxSizing: 'border-box',
            border: '1px dashed rgba(255,255,255,0.95)',
            backgroundColor: 'rgba(70,70,70,0.16)',
            animation: 'fater-grid-preview-pulse 620ms ease-in-out infinite alternate',
          }}
        />
      )}

    </div>,
    portalHost
  );
};

export default React.memo(GridGenerationTilesPreviewOverlay);
